import { useMemo, useState } from 'react';
import { Button, Flex, Text } from '@optiaxiom/react';

import { EmptyPage } from '@frontend/components';
import { ConfirmationModal } from '@frontend/components/confirmationModal';
import { useNamespacedNavigate, useObjectTitle } from '@frontend/hooks';

import { useProductScope } from '../hooks/useProductScope';
import { useUserInstruction } from '../hooks/useUserInstruction';
import { UserInstruction } from '../types';

import { InstructionSearchInput } from './InstructionSearchInput';
import { InstructionTabTable } from './InstructionTabTable';
import Loading from './Loading';

export function InstructionListContent({
  instructionType,
}: {
  instructionType: 'system' | 'user';
}) {
  useObjectTitle(instructionType === 'system' ? 'System Instructions' : 'User Instructions');
  const [filterText, setFilterText] = useState('');
  const [productFilter, setProductFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [instructionToDelete, setInstructionToDelete] = useState<UserInstruction | null>(null);
  const navigate = useNamespacedNavigate();

  const { removeInstruction, userInstructions, isLoading, isRemoving, isError, error } =
    useUserInstruction({
      instructionType,
    });

  const addNewInstruction = () => {
    if (instructionType === 'system') {
      navigate('/instructions/create/system-instruction');
    } else {
      navigate('/instructions/create');
    }
  };

  const handleEditInstruction = (instruction: UserInstruction) => {
    if (instructionType === 'system') {
      navigate(`/instructions/edit/system-instruction/${instruction.instruction_guid}`, {
        state: instruction,
      });
    } else {
      navigate(`/instructions/edit/${instruction.instruction_guid}`, { state: instruction });
    }
  };

  const handleDeleteInstruction = (instruction: UserInstruction) => {
    setInstructionToDelete(instruction);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (instructionToDelete) {
      removeInstruction(instructionToDelete.instruction_guid);
      setDeleteDialogOpen(false);
      setInstructionToDelete(null);
    }
  };

  const availableProducts = useMemo(() => {
    if (!userInstructions) return [];
    const products = new Set<string>();
    userInstructions.forEach((instruction: UserInstruction) => {
      instruction.product_sku.forEach(sku => products.add(sku));
    });
    return Array.from(products).sort();
  }, [userInstructions]);

  const { getProductSku } = useProductScope();
  const filteredInstructions = useMemo(() => {
    return userInstructions?.filter((instruction: UserInstruction) => {
      // Text filter
      if (filterText) {
        const lowerCaseFilter = filterText.toLowerCase();
        const textMatch = instruction.title.toLowerCase().includes(lowerCaseFilter);
        if (!textMatch) return false;
      }

      // Product filter
      if (productFilter.length > 0) {
        const hasMatchingProduct = instruction.product_sku.some(sku => productFilter.includes(sku));
        if (!hasMatchingProduct) return false;
      }

      // Status filter
      if (statusFilter.length > 0) {
        const instructionStatus = instruction.deactivated ? 'inactive' : 'active';
        if (!statusFilter.includes(instructionStatus)) return false;
      }

      return true;
    });
  }, [userInstructions, filterText, productFilter, statusFilter]);

  const hasInstructions = Boolean(filteredInstructions?.length);

  if (isLoading || isRemoving) {
    return <Loading />;
  }

  if (isError) {
    return (
      <Flex flexDirection="column" gap="24">
        <Text>Error: {error as string}</Text>
      </Flex>
    );
  }

  if (!hasInstructions && !filterText && productFilter.length === 0 && statusFilter.length === 0) {
    return (
      <EmptyPage
        title="No instructions found"
        description="Add instructions to fine-tune Opal's performance"
        primaryButtonText="Add Instruction"
        onClickPrimaryButton={addNewInstruction}
      />
    );
  }

  return (
    <Flex flexDirection="column" gap="24">
      <InstructionSearchInput
        filterText={filterText}
        handleFilterChange={setFilterText}
        onClickAddInstruction={addNewInstruction}
        productFilter={productFilter}
        statusFilter={statusFilter}
        availableProducts={availableProducts}
        onProductFilterChange={setProductFilter}
        onStatusFilterChange={setStatusFilter}
      />

      {!hasInstructions ? (
        <Flex flexDirection="column" gap="16" alignItems="center" py="32">
          <Text>No instructions match the current filters</Text>
          <Button
            appearance="subtle"
            onClick={() => {
              setFilterText('');
              setProductFilter([]);
              setStatusFilter([]);
            }}
          >
            Clear all filters
          </Button>
        </Flex>
      ) : (
        <InstructionTabTable
          filteredInstructions={filteredInstructions}
          handleEditInstruction={handleEditInstruction}
          handleDeleteInstruction={handleDeleteInstruction}
          getProductSku={getProductSku}
        />
      )}

      <ConfirmationModal
        isShown={deleteDialogOpen}
        title="Delete Instruction"
        action="Delete"
        appearance="danger"
        onCancel={() => {
          setDeleteDialogOpen(false);
          setInstructionToDelete(null);
        }}
        onConfirm={confirmDelete}
        confirmLoading={isRemoving}
        cancelDisabled={isRemoving}
      >
        <Flex flexDirection="column" gap="4">
          <Text>
            You are about to permanently delete the Instruction{' '}
            <Text fontWeight="600">{instructionToDelete?.title}</Text>
          </Text>
          <Text>Do you want to continue?</Text>
        </Flex>
      </ConfirmationModal>
    </Flex>
  );
}
